import { isApiError } from "@simpro/api-client";
import type { MyAppointmentItem, MyAppointmentStatus } from "../schemas/myAppointment.schemas";

/** Each `AppointmentStatus` in the words a customer reads (FR-SA-052). */
export const VISIT_STATUS: Record<MyAppointmentStatus, { label: string }> = {
  Requested: { label: "Awaiting salon" },
  Confirmed: { label: "Confirmed" },
  Arrived: { label: "You're here" },
  CheckedIn: { label: "Checked in" },
  InQueue: { label: "In the queue" },
  InService: { label: "In progress" },
  Completed: { label: "Completed" },
  Cancelled: { label: "Cancelled" },
  Rejected: { label: "Not accepted by the salon" },
  NoShow: { label: "Missed" },
};

/** `Appointment.ArriveWindow`: how early "I've arrived" is accepted. */
export const ARRIVE_WINDOW_MINUTES = 30;
const ARRIVE_WINDOW_MS = ARRIVE_WINDOW_MINUTES * 60_000;

/** `Feedback.WindowDays`: how long after it ends a visit can be rated. */
export const FEEDBACK_WINDOW_DAYS = 14;

export interface MyVisitActions {
  cancel: boolean;
  reschedule: boolean;
  /** "I've arrived" is shown for a confirmed visit… */
  arrive: boolean;
  /** …and works from half an hour before it starts, as the server allows (follow-up F10). */
  arriveOpen: boolean;
  /** In the line: the position and wait are worth asking for. */
  queue: boolean;
}

/**
 * The transitions the aggregate accepts from each status (Appointment.cs), mirrored only to decide
 * which buttons to show. The server is the judge, including the salon's deadline, and its refusal
 * is shown as sent.
 *
 * - Cancel: anything open except `InService`.
 * - Reschedule: `Requested` or `Confirmed`.
 * - Arrive: `Confirmed`; accepted from `ARRIVE_WINDOW` before the start (Appointment.Arrive), so
 *   before that it shows dimmed.
 * - Queue: `CheckedIn` or `InQueue`.
 */
export function myVisitActions(visit: MyAppointmentItem, now: number): MyVisitActions {
  const { status } = visit;
  const closed =
    status === "Completed" ||
    status === "Cancelled" ||
    status === "Rejected" ||
    status === "NoShow";
  const arrive = status === "Confirmed";

  return {
    cancel: !closed && status !== "InService",
    reschedule: status === "Requested" || status === "Confirmed",
    arrive,
    arriveOpen: arrive && Date.parse(visit.startsAt) - now <= ARRIVE_WINDOW_MS,
    queue: status === "CheckedIn" || status === "InQueue",
  };
}

/** A finished visit, not yet rated, still inside the rating window (BR-SA-015). */
export function canRate(visit: MyAppointmentItem, now: number): boolean {
  return (
    visit.status === "Completed" &&
    !visit.hasFeedback &&
    now - Date.parse(visit.endsAt) <= FEEDBACK_WINDOW_DAYS * 24 * 60 * 60_000
  );
}

/**
 * How a past visit ended, as its card says it (follow-up F14): one card shape, five endings. A visit
 * the customer rated, from this screen or before, is "rated"; the list says so but not how many stars.
 */
export type PastEnding = "completed" | "rated" | "missed" | "notAccepted" | "cancelled";

export function pastEnding(visit: MyAppointmentItem, rated: boolean): PastEnding {
  if (visit.status === "NoShow") return "missed";
  if (visit.status === "Rejected") return "notAccepted";
  if (visit.status === "Cancelled") return "cancelled";
  return visit.hasFeedback || rated ? "rated" : "completed";
}

/** "Haircut + Hair spa": the services in order, as a past card's title. */
export function servicesTitle(visit: MyAppointmentItem): string {
  const names = [...visit.lines].sort((a, b) => a.sequence - b.sequence).map((line) => line.serviceName);
  return names.length > 0 ? names.join(" + ") : "Appointment";
}

/** What a customer reads for a walk-in line still waiting for whoever is free (FR-SA-063). */
export const NEXT_AVAILABLE = "Next available";

/** The distinct stylists of a visit, in the order they do the work. */
export function stylistsOf(visit: MyAppointmentItem): string[] {
  const names = [...visit.lines]
    .sort((a, b) => a.sequence - b.sequence)
    .map((line) => line.staffName?.trim() ?? NEXT_AVAILABLE)
    .filter((name) => name.length > 0);
  return [...new Set(names)];
}

/** The services of a visit in order, as the ids "book again" and rescheduling carry. */
export function serviceIdsOf(visit: MyAppointmentItem): string[] {
  return [...visit.lines].sort((a, b) => a.sequence - b.sequence).map((line) => line.serviceId);
}

/** `/book?services=a,b`: the booking feature reads this parameter to preselect services. */
export function bookAgainPath(serviceIds: readonly string[]): string {
  return `/book?services=${[...new Set(serviceIds)].map(encodeURIComponent).join(",")}`;
}

/** A refusal about the salon's cancel deadline, which deserves the policy and phone number beside it. */
export function isDeadlineRefusal(error: unknown): boolean {
  return isApiError(error) && error.code === "salon.cancel_deadline_passed";
}

/**
 * The words a customer reads for a refusal. The server's message is written for the customer and
 * is used as sent, except where the screen can say what to do next.
 */
export function refusalMessage(error: unknown): string {
  if (!isApiError(error)) return "Something went wrong. Please try again.";
  switch (error.code) {
    case "salon.slot_taken":
      return "That time has just gone. Please pick another.";
    case "salon.feedback_exists":
      return "You have already rated this visit. Thank you.";
    case "salon.feedback_window_passed":
      return `This visit can no longer be rated. Ratings close ${FEEDBACK_WINDOW_DAYS} days after the visit.`;
    case "salon.appointment_not_found":
      return "We could not find this appointment. It may have been changed by the salon.";
    case "salon.not_a_customer":
      return "This page is for customers. Sign in with your mobile number to see your appointments.";
    default:
      return error.message;
  }
}

/** "Haircut", "Haircut and Facial", "Haircut, Facial and Hair spa". */
export function servicesLabel(visit: MyAppointmentItem): string {
  const names = [...visit.lines]
    .sort((a, b) => a.sequence - b.sequence)
    .map((line) => line.serviceName);
  if (names.length <= 1) return names[0] ?? "Appointment";
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1) ?? ""}`;
}
